import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  getAllAssignmentHistory,
  getAmbulanceAssignmentHistory,
  getAmbulances,
  AmbulanceAssignmentHistory,
  Ambulance,
  downloadAssignmentHistoryPDF,
  downloadAssignmentHistoryExcel,
} from '../services/api';
import { formatDateTimeDDMMYYYY, getMaxDateToday, getMaxEndDateForStart } from '../utils/dateUtils';

type DateFilterOption = 'all' | 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const AmbulanceAssignmentHistoryContent: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [history, setHistory] = useState<AmbulanceAssignmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Filters
  const [selectedAmbulanceId, setSelectedAmbulanceId] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<DateFilterOption>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [showAmbulanceDropdown, setShowAmbulanceDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Refs for dropdowns
  const ambulanceDropdownRef = useRef<HTMLDivElement>(null);

  // Dropdown data
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);

  // Tooltip state for staff info
  const [tooltip, setTooltip] = useState<{ x: number; y: number; staffList: Array<{ name: string }> } | null>(null);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedAmbulanceId, dateFilter, customStartDate, customEndDate, searchTerm]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ambulanceDropdownRef.current && !ambulanceDropdownRef.current.contains(event.target as Node)) {
        setShowAmbulanceDropdown(false);
      }
    };

    if (showAmbulanceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAmbulanceDropdown]);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  const fetchDropdownData = async () => {
    try {
      const ambulancesRes = await getAmbulances(1, 1000);
      const allAmbulances = ambulancesRes.data.data || [];
      setAmbulances(allAmbulances);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const getDateRange = (): { startDate?: string; endDate?: string } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilter) {
      case 'all':
        return {};
      
      case 'today': {
        const start = today.getTime();
        const end = today.getTime() + 24 * 60 * 60 * 1000 - 1;
        return {
          startDate: start.toString(),
          endDate: end.toString(),
        };
      }
      
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const start = yesterday.getTime();
        const end = yesterday.getTime() + 24 * 60 * 60 * 1000 - 1;
        return {
          startDate: start.toString(),
          endDate: end.toString(),
        };
      }
      
      case 'thisWeek': {
        // Week starts on Monday (day 1)
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based (Monday = 0)
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - daysToMonday);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
        const start = startOfWeek.getTime();
        const end = endOfWeek.getTime() + 24 * 60 * 60 * 1000 - 1;
        return {
          startDate: start.toString(),
          endDate: end.toString(),
        };
      }
      
      case 'lastWeek': {
        // Week starts on Monday (day 1)
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based (Monday = 0)
        const startOfLastWeek = new Date(today);
        startOfLastWeek.setDate(today.getDate() - daysToMonday - 7); // Go back 7 days from this week's Monday
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6); // Sunday
        const start = startOfLastWeek.getTime();
        const end = endOfLastWeek.getTime() + 24 * 60 * 60 * 1000 - 1;
        return {
          startDate: start.toString(),
          endDate: end.toString(),
        };
      }
      
      case 'thisMonth': {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        return {
          startDate: startOfMonth.getTime().toString(),
          endDate: endOfMonth.getTime().toString(),
        };
      }
      
      case 'lastMonth': {
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return {
          startDate: startOfLastMonth.getTime().toString(),
          endDate: endOfLastMonth.getTime().toString(),
        };
      }
      
      case 'custom': {
        if (customStartDate && customEndDate) {
          const maxEndStr = getMaxEndDateForStart(customStartDate);
          const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
          const start = new Date(customStartDate).getTime();
          const end = new Date(endStr + 'T23:59:59').getTime();
          return {
            startDate: start.toString(),
            endDate: end.toString(),
          };
        }
        return {};
      }
      
      default:
        return {};
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      let response;

      if (selectedAmbulanceId) {
        response = await getAmbulanceAssignmentHistory(
          selectedAmbulanceId,
          currentPage,
          pageSize,
          dateRange.startDate,
          dateRange.endDate,
          searchTerm.trim() || undefined
        );
      } else {
        response = await getAllAssignmentHistory(
          currentPage,
          pageSize,
          selectedAmbulanceId || undefined,
          undefined,
          undefined,
          dateRange.startDate,
          dateRange.endDate,
          undefined,
          searchTerm.trim() || undefined
        );
      }

      const data = response.data.data || [];
      const pagination = response.data.pagination || {};

      setHistory(data);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (error: any) {
      console.error('Failed to fetch assignment history:', error);
      setHistory([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const formatTimestamp = (timestamp: number) => {
    return formatDateTimeDDMMYYYY(timestamp);
  };

  // Filter ambulances based on search term
  const filteredAmbulances = ambulances.filter((amb) => {
    const searchLower = ambulanceSearchTerm.toLowerCase().trim();
    if (searchLower === '') return true;
    
    const name = (amb.metadata.name || '').toLowerCase();
    const licensePlate = (amb.spec.licensePlate || '').toLowerCase();
    const type = (amb.spec.type || '').toLowerCase();
    const vehicleNumber = (amb.spec.vehicleNumber || '').toLowerCase();
    
    return (
      name.includes(searchLower) ||
      licensePlate.includes(searchLower) ||
      type.includes(searchLower) ||
      vehicleNumber.includes(searchLower)
    );
  });

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);

      const dateRange = getDateRange();

      let response;
      let fileExtension;

      if (format === 'pdf') {
        response = await downloadAssignmentHistoryPDF(
          dateRange.startDate,
          dateRange.endDate,
          selectedAmbulanceId || undefined,
          searchTerm.trim() || undefined
        );
        fileExtension = 'pdf';
      } else {
        response = await downloadAssignmentHistoryExcel(
          dateRange.startDate,
          dateRange.endDate,
          selectedAmbulanceId || undefined,
          searchTerm.trim() || undefined
        );
        fileExtension = 'xlsx';
      }

      const { url } = response.data.data;

      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = url;
      link.download = `assignment-history-${dateFilter}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {/* Search and Filters */}
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
        <div style={{ width: 'auto', minWidth: '250px' }}>
          <input
            type="text"
            placeholder="Search by Ambulance, Pilot, or Staff..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
              backgroundColor: isDark ? '#1e293b' : '#fff',
              color: isDark ? '#f1f5f9' : '#000',
              fontSize: '16px',
            }}
          />
        </div>
        <div className="form-group" style={{ width: 'auto', minWidth: '200px' , marginBottom: '-2px'}}>
          <div ref={ambulanceDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showAmbulanceDropdown ? 10001 : 'auto' }}>
            <button
              type="button"
              onClick={() => {
                setShowAmbulanceDropdown(!showAmbulanceDropdown);
                if (!showAmbulanceDropdown) {
                  setAmbulanceSearchTerm('');
                }
              }}
              className="custom-select-button"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid',
                borderColor: isDark ? '#334155' : '#ddd',
                borderRadius: '6px',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '16px',
                backgroundColor: isDark ? '#1e293b' : '#fff',
                color: isDark ? '#f1f5f9' : '#000',
              }}
            >
              <span>
                {(() => {
                  const selectedAmbulance = ambulances.find((a) => a._id === selectedAmbulanceId);
                  return selectedAmbulance
                    ? `${selectedAmbulance.metadata.name} (${selectedAmbulance.spec.licensePlate})`
                    : 'All Ambulances';
                })()}
              </span>
              <span style={{ fontSize: '14px' }}>{showAmbulanceDropdown ? '▲' : '▼'}</span>
            </button>
            {showAmbulanceDropdown && (
              <div
                className="custom-dropdown-menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  borderRadius: '6px',
                  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 10000,
                  maxHeight: '300px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  isolation: 'isolate',
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  border: isDark ? '1px solid #334155' : '1px solid #ddd',
                }}
              >
                <input
                  type="text"
                  placeholder="Search ambulance by name, license plate, or type..."
                  value={ambulanceSearchTerm}
                  onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="search-input"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderBottom: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                    borderRadius: '6px 6px 0 0',
                    fontSize: '16px',
                    outline: 'none',
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                    color: isDark ? '#f1f5f9' : '#000',
                  }}
                  autoFocus
                />
                <div
                  style={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  {(() => {
                    if (filteredAmbulances.length === 0) {
                      return (
                        <div
                          style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: isDark ? '#94a3b8' : '#666',
                            fontSize: '16px',
                          }}
                        >
                          No ambulances found
                        </div>
                      );
                    }

                    return (
                      <>
                        <div
                          onClick={() => {
                            setSelectedAmbulanceId('');
                            setShowAmbulanceDropdown(false);
                            setAmbulanceSearchTerm('');
                            handleFilterChange();
                          }}
                          className={`custom-dropdown-option ${!selectedAmbulanceId ? 'selected' : ''}`}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${isDark ? '#334155' : '#f0f0f0'}`,
                            fontSize: '16px',
                            transition: 'background-color 0.2s',
                            backgroundColor: !selectedAmbulanceId ? (isDark ? '#334155' : '#f3f4f6') : 'transparent',
                            color: isDark ? '#f1f5f9' : '#000',
                          }}
                          onMouseEnter={(e) => {
                            if (selectedAmbulanceId) {
                              e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedAmbulanceId) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          All Ambulances
                        </div>
                        {filteredAmbulances.map((ambulance) => {
                          const handleSelect = () => {
                            setSelectedAmbulanceId(ambulance._id);
                            setShowAmbulanceDropdown(false);
                            setAmbulanceSearchTerm('');
                            handleFilterChange();
                          };

                          return (
                            <div
                              key={ambulance._id}
                              onClick={handleSelect}
                              className={`custom-dropdown-option ${selectedAmbulanceId === ambulance._id ? 'selected' : ''}`}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${isDark ? '#334155' : '#f0f0f0'}`,
                                fontSize: '16px',
                                transition: 'background-color 0.2s',
                                backgroundColor: selectedAmbulanceId === ambulance._id ? (isDark ? '#334155' : '#f3f4f6') : 'transparent',
                                color: isDark ? '#f1f5f9' : '#000',
                              }}
                              onMouseEnter={(e) => {
                                if (selectedAmbulanceId !== ambulance._id) {
                                  e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f9fafb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedAmbulanceId !== ambulance._id) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ width: 'auto', minWidth: '200px' }}>
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value as DateFilterOption);
              handleFilterChange();
            }}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
              backgroundColor: isDark ? '#1e293b' : '#fff',
              color: isDark ? '#f1f5f9' : '#000',
            }}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {dateFilter === 'custom' && (
          <>
            <div style={{ width: 'auto', minWidth: '200px' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomStartDate(value);
                  if (value && customEndDate) {
                    const start = new Date(value);
                    const end = new Date(customEndDate);
                    const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                    if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                  }
                  handleFilterChange();
                }}
                max={getMaxDateToday()}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  color: isDark ? '#f1f5f9' : '#000',
                }}
              />
            </div>
            <div style={{ width: 'auto', minWidth: '200px' }}>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  handleFilterChange();
                }}
                min={customStartDate || undefined}
                max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                  backgroundColor: isDark ? '#1e293b' : '#fff',
                  color: isDark ? '#f1f5f9' : '#000',
                }}
              />
            </div>
          </>
        )}

        {/* Download Button */}
        <div style={{ position: 'relative' }} ref={downloadMenuRef}>
          <button
            onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            disabled={downloading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: downloading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              opacity: downloading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            {downloading ? 'Generating...' : 'Download'}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginLeft: '6px' }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          {showDownloadMenu && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '5px',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                border: isDark ? '1px solid #334155' : '1px solid #ddd',
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                minWidth: '150px',
              }}
            >
              <button
                onClick={() => handleDownload('pdf')}
                disabled={downloading}
                style={{
                  width: '100%',
                  padding: '10px 15px',
                  backgroundColor: 'transparent',
                  color: isDark ? '#ffffff' : '#333',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  textAlign: 'left',
                  opacity: downloading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!downloading) {
                    e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Download as PDF
              </button>
              <button
                onClick={() => handleDownload('excel')}
                disabled={downloading}
                style={{
                  width: '100%',
                  padding: '10px 15px',
                  backgroundColor: 'transparent',
                  color: isDark ? '#ffffff' : '#333',
                  border: 'none',
                  borderTop: isDark ? '1px solid #334155' : '1px solid #ddd',
                  borderRadius: '0 0 6px 6px',
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  textAlign: 'left',
                  opacity: downloading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!downloading) {
                    e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Download as Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Download Error */}
      {downloadError && (
        <div style={{ color: '#ef4444', fontSize: '14px', marginBottom: '10px', textAlign: 'right' }}>
          {downloadError}
        </div>
      )}

      {/* History Table */}
      {loading ? (
        <div className="loading">Loading history...</div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: isDark ? '#94a3b8' : '#6b7280' }}>
          No assignment history found
        </div>
      ) : (
        <>
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: "calc(100vh - 360px)", overflow: 'auto' }}>
              <table className="bookings-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Timestamp</th>
                    <th>Ambulance</th>
                    <th>License Plate</th>
                    <th>Pilot</th>
                    <th>Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => {
                    const staffList = item.spec.assignedStaffs || [];
                    const staffNames = staffList.map(s => s.name).join(', ');
                    const staffDisplayText = staffNames.length > 50 ? staffNames.substring(0, 50) + '...' : staffNames;
                    const showInfoIcon = staffNames.length > 50;

                    return (
                      <tr key={item._id} style={{ height: 'auto' }}>
                        <td style={{ padding: '6px 12px', fontSize: '15px' }}>{formatTimestamp(item.spec.timestamp)}</td>
                        <td style={{ padding: '6px 12px', fontSize: '15px' }}>
                          <strong>{item.spec.ambulanceInfo?.name || 'N/A'}</strong>
                        </td>
                        <td style={{ padding: '6px 12px', fontSize: '15px' }}>
                          {item.spec.ambulanceInfo?.licensePlate ? (
                            <code style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#666' }}>
                              {item.spec.ambulanceInfo.licensePlate}
                            </code>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 12px', fontSize: '15px' }}>
                          {item.spec.assignedPilot ? (
                            item.spec.assignedPilot.name
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 12px', fontSize: '15px' }}>
                          {staffList.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{staffDisplayText}</span>
                              {showInfoIcon && (
                                <span
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTooltip({
                                      x: rect.left + rect.width / 2,
                                      y: rect.top - 5,
                                      staffList: staffList,
                                    });
                                  }}
                                  onMouseLeave={() => setTooltip(null)}
                                  style={{
                                    cursor: 'help',
                                    color: isDark ? '#60a5fa' : '#3b82f6',
                                    fontSize: '16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                  }}
                                  title={staffNames}
                                >
                                  ℹ️
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Tooltip */}
            {tooltip && (
              <div
                style={{
                  position: 'fixed',
                  left: `${tooltip.x}px`,
                  top: `${tooltip.y}px`,
                  transform: 'translate(-50%, -100%)',
                  backgroundColor: isDark ? '#1e293b' : '#0f172a',
                  color: '#ffffff',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  zIndex: 10000,
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  maxWidth: '300px',
                  marginBottom: '5px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '15px' }}>All Staff:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {tooltip.staffList.map((staff, idx) => (
                    <div key={idx} style={{ fontSize: '14px' }}>
                      {staff.name}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: `5px solid ${isDark ? '#1e293b' : '#0f172a'}`,
                  }}
                />
              </div>
            )}

            {/* Pagination */}
            {totalItems > 0 && (
              <div style={{ marginTop: '0', paddingTop: '0' }}>
              <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
                <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                  <label style={{ fontSize: '16px', whiteSpace: 'nowrap', color: isDark ? '#f1f5f9' : '#000' }}>
                    Rows per page:{' '}
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{
                        minWidth: '60px',
                        width: 'auto',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${isDark ? '#334155' : '#ddd'}`,
                        backgroundColor: isDark ? '#1e293b' : '#fff',
                        color: isDark ? '#f1f5f9' : '#000',
                      }}
                    >
                      {[10, 20, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="pagination-center">
                  <span className="pagination-info" style={{ color: isDark ? '#f1f5f9' : '#000' }}>
                    {totalItems === 0
                      ? '0 of 0'
                      : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                          currentPage * pageSize,
                          totalItems
                        )} of ${totalItems}`}
                  </span>
                </div>
                <div className="pagination-right">
                  <button
                    className="btn-secondary"
                    disabled={!hasPreviousPage || loading}
                    onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                  >
                    ‹
                  </button>
                  <button
                    className="btn-secondary"
                    disabled={!hasNextPage || loading}
                    onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AmbulanceAssignmentHistoryContent;

